(() => {
  'use strict';

  const ROOT_SELECTOR = '[data-rrr-cart-premium]';
  const INITIALIZED_ATTRIBUTE = 'data-rrr-cart-js';
  const PENDING_STATE_KEY = '__RRR_CART_PENDING_STATE__';

  class RRRPremiumCart {
    constructor(root) {
      if (
        !root ||
        root.getAttribute(INITIALIZED_ATTRIBUTE) === 'true'
      ) {
        return;
      }

      this.root = root;
      this.root.setAttribute(INITIALIZED_ATTRIBUTE, 'true');

      this.couponSection = this.root.querySelector(
        '[data-rrr-coupon-section]'
      );

      this.couponForm = this.root.querySelector(
        '[data-rrr-coupon-form]'
      );

      this.couponInput = this.root.querySelector(
        '[data-rrr-coupon-input]'
      );

      this.couponApplyButton = this.root.querySelector(
        '[data-rrr-coupon-apply]'
      );

      this.couponButtonLabel = this.root.querySelector(
        '[data-rrr-coupon-button-label]'
      );

      this.couponLoader = this.root.querySelector(
        '[data-rrr-coupon-loader]'
      );

      this.couponStatus = this.root.querySelector(
        '[data-rrr-coupon-status]'
      );

      this.liveRegion = this.root.querySelector(
        '[data-rrr-cart-live-region]'
      );

      this.magicElement = this.root.querySelector(
        '[data-rrr-cart-magic]'
      );

      this.magicSubheading = this.root.querySelector(
        '[data-rrr-magic-subheading]'
      );

      this.surpriseElement = this.root.querySelector(
        '[data-rrr-cart-surprise]'
      );

      this.finalPayableElement = this.root.querySelector(
        '[data-rrr-final-payable]'
      );

      this.texts = this.getConfiguredTexts();

      this.isLoading = false;
      this.magicTimer = null;
      this.surpriseTimer = null;

      this.prefersReducedMotion = window.matchMedia(
        '(prefers-reduced-motion: reduce)'
      ).matches;

      this.handleCouponSubmit =
        this.handleCouponSubmit.bind(this);

      this.handleRootClick =
        this.handleRootClick.bind(this);

      this.bindEvents();
      this.consumePendingState();
    }

    bindEvents() {
      if (this.couponForm) {
        this.couponForm.addEventListener(
          'submit',
          this.handleCouponSubmit
        );
      }

      this.root.addEventListener(
        'click',
        this.handleRootClick
      );
    }

    getConfiguredTexts() {
      const template = this.root.querySelector(
        '[data-rrr-coupon-texts]'
      );

      const textElement =
        template &&
        template.content &&
        template.content.querySelector('span');

      return {
        success:
          textElement?.dataset.success ||
          'Coupon applied successfully',

        invalid:
          textElement?.dataset.invalid ||
          'This coupon is invalid, expired, or not applicable to these products.',

        loading:
          textElement?.dataset.loading ||
          'Unlocking your rare price...',

        apply:
          textElement?.dataset.apply ||
          'APPLY',

        remove:
          textElement?.dataset.remove ||
          'Remove'
      };
    }

    handleCouponSubmit(event) {
      event.preventDefault();

      if (this.isLoading || !this.couponInput) {
        return;
      }

      const code = this.cleanCouponCode(
        this.couponInput.value
      );

      if (!code) {
        this.showError('Please enter a coupon code.');
        this.couponInput.focus();
        return;
      }

      this.applyCoupon(code);
    }

    handleRootClick(event) {
      const removeButton = event.target.closest(
        '[data-rrr-remove-coupon]'
      );

      if (!removeButton || this.isLoading) {
        return;
      }

      event.preventDefault();

      const code = this.cleanCouponCode(
        removeButton.dataset.discountCode || ''
      );

      if (!code) {
        return;
      }

      this.removeCoupon(code);
    }

    async applyCoupon(code) {
      const existingCodes = this.getAppliedCodesFromDOM();

      const requestedCodes = this.getUniqueCodes([
        ...existingCodes,
        code
      ]);

      const previousFinalPayable = this.getNumberData(
        'finalPayable'
      );

      const previousCouponDiscount = this.getNumberData(
        'couponDiscount'
      );

      this.setLoading(true);

      try {
        const cartState = await this.updateDiscountCodes(
          requestedCodes
        );

        const appliedCodes =
          this.extractAppliedCodesFromCart(cartState);

        const couponApplied = appliedCodes.some(
          (appliedCode) =>
            this.normaliseCode(appliedCode) ===
            this.normaliseCode(code)
        );

        if (!couponApplied) {
          this.setLoading(false);
          this.showError(this.texts.invalid);
          return;
        }

        window[PENDING_STATE_KEY] = {
          type: 'coupon-success',
          code,
          previousFinalPayable,
          previousCouponDiscount
        };

        this.refreshCartDrawer(cartState);
      } catch (error) {
        console.error(
          'RRR cart coupon error:',
          error
        );

        this.setLoading(false);

        this.showError(
          error?.message ||
          'Coupon could not be applied. Please try again.'
        );
      }
    }

    async removeCoupon(codeToRemove) {
      const existingCodes = this.getAppliedCodesFromDOM();

      const remainingCodes = existingCodes.filter(
        (code) =>
          this.normaliseCode(code) !==
          this.normaliseCode(codeToRemove)
      );

      this.setLoading(true);

      try {
        const cartState = await this.updateDiscountCodes(
          remainingCodes
        );

        window[PENDING_STATE_KEY] = {
          type: 'coupon-removed',
          code: codeToRemove
        };

        this.refreshCartDrawer(cartState);
      } catch (error) {
        console.error(
          'RRR coupon removal error:',
          error
        );

        this.setLoading(false);

        this.showError(
          'Coupon could not be removed. Please try again.'
        );
      }
    }

    async updateDiscountCodes(codes) {
      const shopifyRoot =
        window.Shopify?.routes?.root || '/';

      const cleanRoot = shopifyRoot.endsWith('/')
        ? shopifyRoot
        : `${shopifyRoot}/`;

      const sectionsUrl =
        `${window.location.pathname}${window.location.search}`;

      const response = await fetch(
        `${cleanRoot}cart/update.js`,
        {
          method: 'POST',
          credentials: 'same-origin',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json'
          },
          body: JSON.stringify({
            discount: codes.join(','),
            sections: [
              'cart-drawer',
              'cart-icon-bubble'
            ],
            sections_url: sectionsUrl
          })
        }
      );

      const responseData = await response.json();

      if (!response.ok) {
        throw new Error(
          responseData?.description ||
          responseData?.message ||
          'Cart update failed.'
        );
      }

      return responseData;
    }

    extractAppliedCodesFromCart(cartState) {
      const codes = [];

      if (
        Array.isArray(cartState?.discount_codes)
      ) {
        cartState.discount_codes.forEach((discount) => {
          if (
            typeof discount === 'string'
          ) {
            codes.push(discount);
            return;
          }

          if (
            discount?.code &&
            discount?.applicable !== false
          ) {
            codes.push(discount.code);
          }
        });
      }

      if (
        Array.isArray(
          cartState?.cart_level_discount_applications
        )
      ) {
        cartState.cart_level_discount_applications.forEach(
          (application) => {
            if (
              application?.type === 'discount_code' &&
              application?.title
            ) {
              codes.push(application.title);
            }
          }
        );
      }

      if (Array.isArray(cartState?.items)) {
        cartState.items.forEach((item) => {
          const allocations =
            item?.line_level_discount_allocations ||
            item?.discount_allocations ||
            [];

          allocations.forEach((allocation) => {
            const application =
              allocation?.discount_application;

            if (
              application?.type === 'discount_code' &&
              application?.title
            ) {
              codes.push(application.title);
            }
          });
        });
      }

      return this.getUniqueCodes(codes);
    }

    getAppliedCodesFromDOM() {
      return this.getUniqueCodes(
        Array.from(
          this.root.querySelectorAll(
            '[data-rrr-applied-coupon]'
          )
        )
          .map((element) =>
            this.cleanCouponCode(
              element.dataset.discountCode || ''
            )
          )
          .filter(Boolean)
      );
    }

    refreshCartDrawer(cartState) {
      const cartDrawer =
        document.querySelector('cart-drawer');

      /*
       * Dawn ka native renderContents method use karenge.
       * Isse quantity controls, remove buttons aur cart bubble
       * native Dawn flow ke through refresh hote hain.
       */
      if (
        cartDrawer &&
        typeof cartDrawer.renderContents === 'function' &&
        cartState?.sections?.['cart-drawer']
      ) {
        cartDrawer.renderContents(cartState);

        window.setTimeout(() => {
          initialisePremiumCarts(document);
        }, 50);

        return;
      }

      /*
       * Fallback: returned CartDrawer HTML manually replace karo.
       */
      const drawerHtml =
        cartState?.sections?.['cart-drawer'];

      if (drawerHtml) {
        const parser = new DOMParser();

        const parsedDocument = parser.parseFromString(
          drawerHtml,
          'text/html'
        );

        const incomingCartDrawer =
          parsedDocument.querySelector('#CartDrawer');

        const currentCartDrawer =
          document.querySelector('#CartDrawer');

        if (
          incomingCartDrawer &&
          currentCartDrawer
        ) {
          currentCartDrawer.innerHTML =
            incomingCartDrawer.innerHTML;
        }
      }

      this.replaceCartBubble(
        cartState?.sections?.['cart-icon-bubble']
      );

      window.setTimeout(() => {
        initialisePremiumCarts(document);

        const drawer =
          document.querySelector('cart-drawer');

        if (
          drawer &&
          typeof drawer.open === 'function'
        ) {
          drawer.open();
        }
      }, 50);
    }

    replaceCartBubble(sectionHtml) {
      if (!sectionHtml) {
        return;
      }

      const parser = new DOMParser();

      const parsedDocument = parser.parseFromString(
        sectionHtml,
        'text/html'
      );

      const incomingBubble =
        parsedDocument.querySelector(
          '#shopify-section-cart-icon-bubble'
        ) ||
        parsedDocument.querySelector(
          '#cart-icon-bubble'
        );

      const currentBubble =
        document.querySelector(
          '#shopify-section-cart-icon-bubble'
        ) ||
        document.querySelector(
          '#cart-icon-bubble'
        );

      if (
        incomingBubble &&
        currentBubble
      ) {
        currentBubble.innerHTML =
          incomingBubble.innerHTML;
      }
    }

    consumePendingState() {
      const pendingState =
        window[PENDING_STATE_KEY];

      if (!pendingState) {
        return;
      }

      delete window[PENDING_STATE_KEY];

      window.setTimeout(() => {
        if (
          pendingState.type ===
          'coupon-success'
        ) {
          this.handleSuccessfulCoupon(
            pendingState
          );

          return;
        }

        if (
          pendingState.type ===
          'coupon-removed'
        ) {
          this.showStatus(
            'Coupon removed successfully.',
            'success'
          );
        }
      }, 80);
    }

    handleSuccessfulCoupon(state) {
      const currentCouponDiscount =
        this.getNumberData('couponDiscount');

      const currentFinalPayable =
        this.getNumberData('finalPayable');

      const addedSaving = Math.max(
        currentCouponDiscount -
          Number(state.previousCouponDiscount || 0),
        0
      );

      if (this.couponInput) {
        this.couponInput.value = '';
      }

      this.showStatus(
        `${this.texts.success}${
          addedSaving > 0
            ? ` — You saved ${this.formatMoney(addedSaving)}`
            : ''
        }`,
        'success'
      );

      if (this.couponSection) {
        this.couponSection.classList.add(
          'is-success'
        );

        window.setTimeout(() => {
          this.couponSection?.classList.remove(
            'is-success'
          );
        }, 3200);
      }

      if (
        this.root.dataset.magicEnabled === 'true'
      ) {
        this.playGoldenUnlock(
          Number(state.previousFinalPayable || 0),
          currentFinalPayable
        );
      } else {
        this.revealSurprise();
      }
    }

    playGoldenUnlock(
      previousFinalPayable,
      currentFinalPayable
    ) {
      if (!this.magicElement) {
        this.revealSurprise();
        return;
      }

      window.clearTimeout(this.magicTimer);
      window.clearTimeout(this.surpriseTimer);

      const duration = Math.max(
        Number.parseInt(
          this.root.dataset.magicDuration || '1800',
          10
        ),
        300
      );

      this.magicElement.hidden = false;

      this.magicElement.classList.remove(
        'is-active',
        'is-shimmering',
        'is-particles-active'
      );

      window.requestAnimationFrame(() => {
        window.requestAnimationFrame(() => {
          this.magicElement.classList.add(
            'is-active'
          );

          if (
            this.root.dataset.magicShimmer ===
            'true' &&
            !this.prefersReducedMotion
          ) {
            this.magicElement.classList.add(
              'is-shimmering'
            );
          }

          if (
            this.root.dataset.magicParticles ===
            'true' &&
            !this.prefersReducedMotion
          ) {
            this.magicElement.classList.add(
              'is-particles-active'
            );
          }
        });
      });

      if (
        this.finalPayableElement &&
        this.root.dataset.magicPriceRoll ===
          'true'
      ) {
        this.finalPayableElement.classList.add(
          'is-rolling'
        );

        if (!this.prefersReducedMotion) {
          this.animateMoney(
            this.finalPayableElement,
            previousFinalPayable,
            currentFinalPayable,
            Math.min(duration, 1200)
          );
        }
      }

      this.announce(
        this.texts.success
      );

      this.magicTimer = window.setTimeout(() => {
        this.magicElement.classList.remove(
          'is-active',
          'is-shimmering',
          'is-particles-active'
        );

        this.finalPayableElement?.classList.remove(
          'is-rolling'
        );

        if (this.finalPayableElement) {
          this.finalPayableElement.textContent =
            this.formatMoney(
              currentFinalPayable,
              true
            );
        }

        window.setTimeout(() => {
          if (!this.magicElement) {
            return;
          }

          this.magicElement.hidden = true;
        }, 220);

        this.revealSurprise();
      }, this.prefersReducedMotion ? 400 : duration);
    }

    revealSurprise() {
      if (!this.surpriseElement) {
        return;
      }

      this.surpriseElement.hidden = false;

      window.requestAnimationFrame(() => {
        window.requestAnimationFrame(() => {
          this.surpriseElement.classList.add(
            'is-visible'
          );
        });
      });
    }

    animateMoney(
      element,
      startValue,
      endValue,
      duration
    ) {
      const safeStart =
        Number.isFinite(startValue)
          ? startValue
          : endValue;

      const safeEnd =
        Number.isFinite(endValue)
          ? endValue
          : safeStart;

      const startTime = performance.now();

      const updateFrame = (currentTime) => {
        const elapsed =
          currentTime - startTime;

        const progress = Math.min(
          elapsed / duration,
          1
        );

        const easedProgress =
          1 - Math.pow(1 - progress, 3);

        const currentValue = Math.round(
          safeStart +
          (safeEnd - safeStart) *
            easedProgress
        );

        element.textContent =
          this.formatMoney(
            currentValue,
            true
          );

        if (progress < 1) {
          window.requestAnimationFrame(
            updateFrame
          );
        }
      };

      window.requestAnimationFrame(
        updateFrame
      );
    }

    setLoading(isLoading) {
      this.isLoading = isLoading;

      if (this.couponSection) {
        this.couponSection.classList.toggle(
          'is-loading',
          isLoading
        );

        if (isLoading) {
          this.couponSection.classList.remove(
            'is-error',
            'is-success'
          );
        }
      }

      if (this.couponApplyButton) {
        this.couponApplyButton.disabled =
          isLoading;
      }

      if (this.couponInput) {
        this.couponInput.disabled =
          isLoading;
      }

      if (this.couponLoader) {
        this.couponLoader.hidden =
          !isLoading;
      }

      if (this.couponButtonLabel) {
        this.couponButtonLabel.hidden =
          isLoading;
      }

      if (isLoading) {
        this.showStatus(
          this.texts.loading,
          'loading'
        );
      }
    }

    showStatus(message, type = '') {
      if (!this.couponStatus) {
        return;
      }

      this.couponStatus.textContent =
        message || '';

      this.couponStatus.classList.remove(
        'is-success',
        'is-error'
      );

      if (type === 'success') {
        this.couponStatus.classList.add(
          'is-success'
        );
      }

      if (type === 'error') {
        this.couponStatus.classList.add(
          'is-error'
        );
      }

      this.announce(message);
    }

    showError(message) {
      this.showStatus(message, 'error');

      if (!this.couponSection) {
        return;
      }

      this.couponSection.classList.remove(
        'is-loading',
        'is-success'
      );

      this.couponSection.classList.add(
        'is-error',
        'is-shaking'
      );

      window.setTimeout(() => {
        this.couponSection?.classList.remove(
          'is-shaking'
        );
      }, 500);

      window.setTimeout(() => {
        this.couponSection?.classList.remove(
          'is-error'
        );
      }, 3500);
    }

    announce(message) {
      if (!this.liveRegion) {
        return;
      }

      this.liveRegion.textContent = '';

      window.setTimeout(() => {
        if (this.liveRegion) {
          this.liveRegion.textContent =
            message || '';
        }
      }, 30);
    }

    cleanCouponCode(code) {
      return String(code || '')
        .trim()
        .replace(/\s+/g, '')
        .toUpperCase();
    }

    normaliseCode(code) {
      return this.cleanCouponCode(code)
        .toLowerCase();
    }

    getUniqueCodes(codes) {
      const seenCodes = new Set();

      return codes.filter((code) => {
        const cleanedCode =
          this.cleanCouponCode(code);

        if (!cleanedCode) {
          return false;
        }

        const normalisedCode =
          this.normaliseCode(cleanedCode);

        if (seenCodes.has(normalisedCode)) {
          return false;
        }

        seenCodes.add(normalisedCode);
        return true;
      });
    }

    getNumberData(key) {
      const value = Number.parseInt(
        this.root.dataset[key] || '0',
        10
      );

      return Number.isFinite(value)
        ? value
        : 0;
    }

    formatMoney(cents, withCurrency = false) {
      const moneyFormat =
        this.root.dataset.moneyFormat ||
        '₹{{amount}}';

      const currency =
        this.root.dataset.currency || 'INR';

      let format = moneyFormat;

      if (
        withCurrency &&
        !format.includes(currency)
      ) {
        format = `${format} ${currency}`;
      }

      const placeholderMatch = format.match(
        /\{\{\s*(\w+)\s*\}\}/
      );

      if (!placeholderMatch) {
        return `${(cents / 100).toFixed(2)} ${
          withCurrency ? currency : ''
        }`.trim();
      }

      const placeholder =
        placeholderMatch[1];

      let formattedAmount;

      switch (placeholder) {
        case 'amount_no_decimals':
          formattedAmount =
            this.formatWithDelimiters(
              cents / 100,
              0,
              ',',
              '.'
            );
          break;

        case 'amount_with_comma_separator':
          formattedAmount =
            this.formatWithDelimiters(
              cents / 100,
              2,
              '.',
              ','
            );
          break;

        case 'amount_no_decimals_with_comma_separator':
          formattedAmount =
            this.formatWithDelimiters(
              cents / 100,
              0,
              '.',
              ','
            );
          break;

        case 'amount_with_apostrophe_separator':
          formattedAmount =
            this.formatWithDelimiters(
              cents / 100,
              2,
              "'",
              '.'
            );
          break;

        case 'amount':
        default:
          formattedAmount =
            this.formatWithDelimiters(
              cents / 100,
              2,
              ',',
              '.'
            );
          break;
      }

      const formattedMoney = format.replace(
        placeholderMatch[0],
        formattedAmount
      );

      const temporaryElement =
        document.createElement('div');

      temporaryElement.innerHTML =
        formattedMoney;

      return (
        temporaryElement.textContent ||
        temporaryElement.innerText ||
        formattedMoney
      ).trim();
    }

    formatWithDelimiters(
      number,
      precision = 2,
      thousands = ',',
      decimal = '.'
    ) {
      if (
        number === null ||
        number === undefined ||
        Number.isNaN(Number(number))
      ) {
        return '0';
      }

      const fixedNumber = Number(number).toFixed(
        precision
      );

      const parts = fixedNumber.split('.');

      const wholeNumber = parts[0].replace(
        /(\d)(?=(\d\d\d)+(?!\d))/g,
        `$1${thousands}`
      );

      const decimalNumber =
        parts[1]
          ? `${decimal}${parts[1]}`
          : '';

      return `${wholeNumber}${decimalNumber}`;
    }
  }

  function initialisePremiumCarts(scope = document) {
    const roots = [];

    if (
      scope.matches &&
      scope.matches(ROOT_SELECTOR)
    ) {
      roots.push(scope);
    }

    if (scope.querySelectorAll) {
      roots.push(
        ...scope.querySelectorAll(
          ROOT_SELECTOR
        )
      );
    }

    roots.forEach((root) => {
      if (
        root.getAttribute(
          INITIALIZED_ATTRIBUTE
        ) !== 'true'
      ) {
        root.rrrPremiumCart =
          new RRRPremiumCart(root);
      }
    });
  }

  function initialiseDocument() {
    initialisePremiumCarts(document);
  }

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      initialiseDocument,
      { once: true }
    );
  } else {
    initialiseDocument();
  }

  /*
   * Dawn quantity/remove update ke baad drawer HTML replace karta hai.
   * Observer new premium summary ko automatically initialize karega.
   */
  const cartObserver = new MutationObserver(
    (mutations) => {
      const premiumCartAdded =
        mutations.some((mutation) =>
          Array.from(mutation.addedNodes).some(
            (node) => {
              if (
                node.nodeType !==
                Node.ELEMENT_NODE
              ) {
                return false;
              }

              return (
                node.matches?.(
                  ROOT_SELECTOR
                ) ||
                node.querySelector?.(
                  ROOT_SELECTOR
                )
              );
            }
          )
        );

      if (premiumCartAdded) {
        initialisePremiumCarts(document);
      }
    }
  );

  if (document.body) {
    cartObserver.observe(document.body, {
      childList: true,
      subtree: true
    });
  } else {
    document.addEventListener(
      'DOMContentLoaded',
      () => {
        cartObserver.observe(document.body, {
          childList: true,
          subtree: true
        });
      },
      { once: true }
    );
  }

  document.addEventListener(
    'shopify:section:load',
    (event) => {
      initialisePremiumCarts(event.target);
    }
  );
      /* ================================================================
     RRR MOBILE CART — PERSISTENT INTERACTIVE LAYERS
     Active layer survives AJAX / soft cart refresh
     ================================================================ */

  const RRR_MOBILE_LAYER_STATE_KEY =
    '__RRR_CART_MOBILE_LAYER_STATE__';


  function getRRRMobileLayerState() {
    return window[RRR_MOBILE_LAYER_STATE_KEY] === 'summary'
      ? 'summary'
      : 'products';
  }


  function applyRRRMobileLayerState(scope = document) {
    

    const drawerInners = [];

    if (
      scope instanceof Element &&
      scope.matches(
        '.drawer__inner.rrr-cart-premium-enabled'
      )
    ) {
      drawerInners.push(scope);
    }

    if (scope.querySelectorAll) {
      drawerInners.push(
        ...scope.querySelectorAll(
          '.drawer__inner.rrr-cart-premium-enabled'
        )
      );
    }

    const activeState =
      getRRRMobileLayerState();

    drawerInners.forEach((drawerInner) => {
      drawerInner.classList.toggle(
        'rrr-mobile-summary-active',
        activeState === 'summary'
      );
    });
  }


  function setRRRMobileLayerState(
    state,
    drawerInner
  ) {
    const safeState =
      state === 'summary'
        ? 'summary'
        : 'products';

    window[RRR_MOBILE_LAYER_STATE_KEY] =
      safeState;

    if (drawerInner) {
      drawerInner.classList.toggle(
        'rrr-mobile-summary-active',
        safeState === 'summary'
      );
    }
  }


  function handleMobileCartLayerFocus(event) {
    

    const target = event.target;

    if (!(target instanceof Element)) {
      return;
    }

    const drawerInner = target.closest(
      '.drawer__inner.rrr-cart-premium-enabled'
    );

    if (!drawerInner) {
      return;
    }

    const productLayer = target.closest(
      'cart-drawer-items'
    );

    const summaryLayer = target.closest(
      '.drawer__footer.rrr-cart-premium-footer'
    );


    /* Product layer selected */
    if (productLayer) {
      setRRRMobileLayerState(
        'products',
        drawerInner
      );

      return;
    }


    /* Summary layer selected */
    if (summaryLayer) {
      setRRRMobileLayerState(
        'summary',
        drawerInner
      );
    }
  }


  document.addEventListener(
    'pointerdown',
    handleMobileCartLayerFocus,
    {
      passive: true
    }
  );


  /*
   * Whenever Dawn replaces cart drawer HTML,
   * restore whichever layer was active before refresh.
   */
  const rrrMobileLayerObserver =
    new MutationObserver(() => {

      window.requestAnimationFrame(() => {
        applyRRRMobileLayerState(document);
      });

    });


  function startRRRMobileLayerObserver() {
    const cartDrawer =
      document.querySelector('cart-drawer');

    if (!cartDrawer) {
      return;
    }

    rrrMobileLayerObserver.observe(
      cartDrawer,
      {
        childList: true,
        subtree: true
      }
    );

    applyRRRMobileLayerState(document);
  }


  if (document.readyState === 'loading') {

    document.addEventListener(
      'DOMContentLoaded',
      startRRRMobileLayerObserver,
      {
        once: true
      }
    );

  } else {

    startRRRMobileLayerObserver();

  }


  document.addEventListener(
    'shopify:section:load',
    () => {
      window.requestAnimationFrame(() => {
        applyRRRMobileLayerState(document);
      });
    }
  );
})();